import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, User, Package, AlertCircle, FileText, ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';
import FormField from './FormField';
import sampleData from '../data/sampleData.json';
import ApiService from '../services/api';

const ComplaintForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false);
  const [apiData, setApiData] = useState({
    users: [],
    products: [],
    batches: []
  });
  const { register, handleSubmit, control, formState: { errors }, trigger, watch } = useForm({
    defaultValues: {
      production_date: new Date().toISOString().split('T')[0],
      status: 'Reported'
    }
  });

  // Watch all form values to track completion
  const formValues = watch();

  // Fetch data from APIs on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [customersResponse, productsResponse, batchesResponse] = await Promise.all([
          ApiService.getCustomers().catch(() => sampleData.customers),
          ApiService.getProducts().catch(() => sampleData.products),
          ApiService.getBatches().catch(() => sampleData.batches)
        ]);

        // Normalize the data to handle different API response formats
        const normalizedUsers = Array.isArray(customersResponse) ? customersResponse : sampleData.customers;
        const normalizedProducts = Array.isArray(productsResponse) ? productsResponse : sampleData.products;
        const normalizedBatches = Array.isArray(batchesResponse) ? batchesResponse : sampleData.batches;

        setApiData({
          users: normalizedUsers,
          products: normalizedProducts,
          batches: normalizedBatches
        });
      } catch (error) {
        toast.error('Failed to load form data. Using default values.');
        setApiData({
          users: sampleData.customers,
          products: sampleData.products,
          batches: sampleData.batches
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Define form steps
  const steps = [
    {
      title: 'Customer Information',
      icon: User,
      fields: ['customer_id', 'customer_contact_details'],
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=800&fit=crop'
    },
    {
      title: 'Product Details',
      icon: Package,
      fields: ['product_id', 'batch_id', 'production_date'],
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=800&fit=crop'
    },
    {
      title: 'Complaint Details',
      icon: AlertCircle,
      fields: ['defect_description', 'severity', 'business_impact'],
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=800&fit=crop'
    },
    {
      title: 'Additional Information',
      icon: FileText,
      fields: ['cost_estimate', 'status', 'previous_complaint_id', 'linked_issue_id', 'is_repeat'],
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&h=800&fit=crop'
    }
  ];

  const currentStepData = steps[currentStep];

  const nextStep = async () => {
    console.log('nextStep called, currentStep:', currentStep);

    // Prevent navigation if already navigating
    if (isNavigating) {
      console.log('Already navigating, ignoring');
      return;
    }

    setIsNavigating(true);

    // Only validate required fields for each step
    const requiredFieldsByStep = {
      0: ['customer_id', 'customer_contact_details'],
      1: ['product_id', 'batch_id'],
      2: ['defect_description', 'severity', 'business_impact'],
      3: ['cost_estimate'] // Cost Estimate is required in step 4
    };

    const fieldsToValidate = requiredFieldsByStep[currentStep];

    // If there are no required fields, just move to next step
    if (fieldsToValidate.length === 0) {
      if (currentStep < steps.length - 1) {
        console.log('Moving to next step (no validation needed)');
        setCurrentStep(currentStep + 1);
      }
      setTimeout(() => setIsNavigating(false), 100);
      return;
    }

    const isValid = await trigger(fieldsToValidate);
    console.log('Validation result:', isValid);

    if (isValid) {
      if (currentStep < steps.length - 1) {
        console.log('Moving to next step');
        setCurrentStep(currentStep + 1);
      }
    } else {
      console.log('Validation failed, staying on current step');
    }

    setTimeout(() => setIsNavigating(false), 100);
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async (data) => {
    console.log('onSubmit called, currentStep:', currentStep, 'steps.length:', steps.length);

    // Block submission if navigating between steps
    if (isNavigating) {
      console.log('Blocked submission - currently navigating');
      return;
    }

    // Only allow submission from the last step
    if (currentStep !== steps.length - 1) {
      console.log('Blocked submission - not on last step');
      return;
    }

    // Prevent double submission
    if (isSubmitting) {
      console.log('Blocked submission - already submitting');
      return;
    }

    console.log('Proceeding with submission');
    setIsSubmitting(true);

    try {
      const result = await ApiService.submitComplaint(data);

      localStorage.setItem('complaintData', JSON.stringify({
        ...data,
        complaint_id: result.complaint_id || Math.floor(Math.random() * 10000) + 1000,
        submission_date: new Date().toISOString(),
        api_response: result
      }));

      toast.success('Complaint submitted successfully!');
      navigate('/thank-you');
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.message || 'Failed to submit complaint. Please try again.';

      // Handle specific error types
      if (errorMessage.includes('duplicate key')) {
        toast.error('Unable to submit - this may be a duplicate. Please try again or contact support if the issue persists.', {
          duration: 5000
        });
      } else if (errorMessage.includes('500')) {
        toast.error('Server error occurred. Please try again in a moment.', {
          duration: 5000
        });
      } else {
        toast.error(errorMessage, {
          duration: 5000
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card p-6">
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <span className="text-gray-600">Loading form data...</span>
          </div>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            <FormField
              label="Customer ID"
              name="customer_id"
              type="select"
              register={register}
              control={control}
              error={errors.customer_id}
              required
              options={apiData.users.map(user => {
                const customerId = user.id || user.customer_id || user["Customer ID"];
                const customerName = user.name || user.customer_name || user["Customer Name"];
                return {
                  value: customerId,
                  label: customerName ? `${customerId} - ${customerName}` : String(customerId)
                };
              })}
              placeholder="Search and select customer..."
            />
            <FormField
              label="Contact Email"
              name="customer_contact_details"
              type="email"
              register={register}
              error={errors.customer_contact_details}
              required
              placeholder="customer@example.com"
            />
          </>
        );
      case 1:
        return (
          <>
            <FormField
              label="Product"
              name="product_id"
              type="select"
              register={register}
              control={control}
              error={errors.product_id}
              required
              options={apiData.products.map(product => {
                const productId = product.id || product.product_id || product["Product ID"];
                const productName = product.product_name || product.name || product["Product Name"];
                const productCategory = product.category || product.product_category || product["Product Category"];
                return {
                  value: productId,
                  label: productCategory ? `${productName} (${productCategory})` : String(productName || productId)
                };
              })}
              placeholder="Search and select product..."
            />
            <FormField
              label="Batch"
              name="batch_id"
              type="select"
              register={register}
              control={control}
              error={errors.batch_id}
              required
              options={apiData.batches.map(batch => {
                const batchId = batch.id || batch.batch_id || batch["Batch ID"];
                const batchName = batch.batch_name || batch.batch_number || batch["Batch Number"] || batch.name;
                const productName = batch.product_name || batch["Product Name"];

                let label;
                if (batchName && productName) {
                  label = `${batchName} - ${productName}`;
                } else if (batchName) {
                  label = String(batchName);
                } else {
                  label = `Batch ${batchId}`;
                }

                return {
                  value: batchId,
                  label: label
                };
              })}
              placeholder="Search and select batch..."
            />
            <FormField
              label="Production Date"
              name="production_date"
              type="date"
              register={register}
              error={errors.production_date}
            />
          </>
        );
      case 2:
        return (
          <>
            <FormField
              label="Defect Description"
              name="defect_description"
              type="textarea"
              register={register}
              error={errors.defect_description}
              required
              placeholder="Please describe the defect or issue in detail..."
              className="sm:col-span-2"
            />
            <FormField
              label="Severity Level"
              name="severity"
              type="select"
              register={register}
              control={control}
              error={errors.severity}
              required
              options={sampleData.severityLevels}
              placeholder="Search and select severity..."
            />
            <FormField
              label="Business Impact"
              name="business_impact"
              type="select"
              register={register}
              control={control}
              error={errors.business_impact}
              required
              options={sampleData.businessImpactTypes}
              placeholder="Search and select impact type..."
            />
          </>
        );
      case 3:
        return (
          <>
            <FormField
              label="Cost Estimate (USD)"
              name="cost_estimate"
              type="number"
              register={register}
              error={errors.cost_estimate}
              required
              placeholder="0.00"
              step="0.01"
              min="0"
            />
            <FormField
              label="Status"
              name="status"
              type="select"
              register={register}
              control={control}
              error={errors.status}
              options={sampleData.statusOptions}
              placeholder="Search and select status..."
            />
            <FormField
              label="Previous Complaint ID"
              name="previous_complaint_id"
              type="number"
              register={register}
              error={errors.previous_complaint_id}
              placeholder="Enter previous complaint ID if applicable"
            />
            <FormField
              label="Linked Issue ID"
              name="linked_issue_id"
              type="number"
              register={register}
              error={errors.linked_issue_id}
              placeholder="Enter linked issue ID if applicable"
            />
            <div className="sm:col-span-2">
              <FormField
                name="is_repeat"
                type="checkbox"
                register={register}
                placeholder="This is a repeat defect"
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  // Calculate filled fields
  const totalRequiredFields = ['customer_id', 'customer_contact_details', 'product_id', 'batch_id', 'defect_description', 'severity', 'business_impact', 'cost_estimate'];
  const filledFields = totalRequiredFields.filter(field => formValues[field]).length;

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4">
      {/* Progress Bar */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs sm:text-sm font-medium text-gray-700">Progress</span>
          <span className="text-xs sm:text-sm font-medium text-primary-600">{Math.round(progressPercentage)}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-primary-500 to-primary-600"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div className={`
                  w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-300
                  ${index < currentStep ? 'bg-green-500 text-white' :
                    index === currentStep ? 'bg-primary-600 text-white ring-2 sm:ring-4 ring-primary-100' :
                      'bg-gray-200 text-gray-500'}
                `}>
                  {index < currentStep ? (
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                  ) : (
                    <step.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  )}
                </div>
                <span className={`mt-1 sm:mt-1.5 text-[10px] sm:text-xs font-medium hidden sm:block ${index === currentStep ? 'text-primary-600' : 'text-gray-500'
                  }`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`h-0.5 flex-1 mx-1 sm:mx-2 rounded transition-all duration-300 ${index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Left Side - Form */}
        <motion.div
          className="card p-3 sm:p-5"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-3 sm:mb-5">
            <div className="flex items-center justify-between mb-1.5 sm:mb-1">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <div className="p-1 sm:p-2 bg-primary-600 rounded-md sm:rounded-lg">
                  <currentStepData.icon className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                </div>
                <h2 className="text-sm sm:text-xl font-bold text-gray-900 leading-tight">{currentStepData.title}</h2>
              </div>
              <span className="text-[9px] sm:text-xs text-gray-500 whitespace-nowrap ml-1">Step {currentStep + 1}/{steps.length}</span>
            </div>
            <div className="flex flex-col space-y-0.5 sm:space-y-1">
              <p className="text-[9px] sm:text-xs text-gray-500 leading-snug">
                {currentStep === 0 && "Provide your customer details to help us identify and contact you."}
                {currentStep === 1 && "Select the product and batch related to your complaint."}
                {currentStep === 2 && "Describe the issue and its severity to help us prioritize."}
                {currentStep === 3 && "Add any additional information that might be relevant."}
              </p>
              <span className="text-[9px] sm:text-xs text-gray-400">
                {filledFields}/{totalRequiredFields.length} required
              </span>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Form onSubmit triggered, currentStep:', currentStep);
              // Only submit if on the last step
              if (currentStep === steps.length - 1) {
                console.log('Calling handleSubmit');
                handleSubmit(onSubmit)(e);
              } else {
                console.log('Blocked form submission - not on last step');
              }
            }}
            onKeyDown={(e) => {
              // Handle Enter key
              if (e.key === 'Enter' && e.target.type !== 'textarea') {
                e.preventDefault();
                e.stopPropagation();
                console.log('Enter key pressed, currentStep:', currentStep);
                if (currentStep < steps.length - 1) {
                  console.log('Calling nextStep from Enter key');
                  nextStep();
                } else {
                  console.log('On last step - Enter key does nothing');
                }
              }
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col gap-3 sm:grid sm:grid-cols-2 sm:gap-4"
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-3 sm:mt-6 pt-3 sm:pt-5 border-t border-gray-200 gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 0}
                className={`btn-secondary flex items-center justify-center space-x-0.5 sm:space-x-1.5 text-[11px] sm:text-sm py-1.5 sm:py-3 px-2.5 sm:px-6 min-w-[70px] sm:min-w-auto ${currentStep === 0 ? 'invisible' : ''
                  }`}
              >
                <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Previous</span>
              </button>

              {currentStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn-primary flex items-center justify-center space-x-0.5 sm:space-x-1.5 text-[11px] sm:text-sm py-1.5 sm:py-3 px-2.5 sm:px-6 min-w-[70px] sm:min-w-auto"
                >
                  <span>Next</span>
                  <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex items-center justify-center space-x-0.5 sm:space-x-1.5 min-w-[80px] sm:min-w-[130px] text-[11px] sm:text-sm py-1.5 sm:py-3 px-2.5 sm:px-6"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                      <span className="hidden xs:inline">Submitting...</span>
                      <span className="xs:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Submit</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </motion.div>

        {/* Right Side - Image */}
        <motion.div
          className="hidden lg:block"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="sticky top-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="relative rounded-xl overflow-hidden shadow-xl"
                style={{ height: '550px' }}
              >
                <img
                  src={currentStepData.image}
                  alt={currentStepData.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <h3 className="text-xl font-bold mb-1.5">{currentStepData.title}</h3>
                  <p className="text-sm text-gray-200 leading-relaxed">
                    {currentStep === 0 && "Provide your customer details to help us identify and contact you."}
                    {currentStep === 1 && "Select the product and batch related to your complaint."}
                    {currentStep === 2 && "Describe the issue and its severity to help us prioritize."}
                    {currentStep === 3 && "Add any additional information that might be relevant."}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ComplaintForm;